// CSS named colors (name + hex). Source: W3C/CSS Color Module Level 4.
// Saved as hex so we can render the swatch and store a stable value.
//
// Sorted by hue (a rainbow) so the picker reads as a spectrum; achromatic
// colors (black/grays/whites) are grouped at the end, dark to light.
//
// Note: IndianRed, DeepPink, and White were intentionally removed from the
// device-group picker — re-add them here if that decision is reversed.
export type NamedColor = { name: string; hex: string }

export const CSS_COLOR_NAMES: NamedColor[] = [
  { name: 'Maroon', hex: '#800000' },
  { name: 'DarkRed', hex: '#8B0000' },
  { name: 'Brown', hex: '#A52A2A' },
  { name: 'FireBrick', hex: '#B22222' },
  { name: 'Red', hex: '#FF0000' },
  { name: 'RosyBrown', hex: '#BC8F8F' },
  { name: 'LightCoral', hex: '#F08080' },
  { name: 'Snow', hex: '#FFFAFA' },
  { name: 'MistyRose', hex: '#FFE4E1' },
  { name: 'Salmon', hex: '#FA8072' },
  { name: 'Tomato', hex: '#FF6347' },
  { name: 'DarkSalmon', hex: '#E9967A' },
  { name: 'Coral', hex: '#FF7F50' },
  { name: 'OrangeRed', hex: '#FF4500' },
  { name: 'LightSalmon', hex: '#FFA07A' },
  { name: 'Sienna', hex: '#A0522D' },
  { name: 'SeaShell', hex: '#FFF5EE' },
  { name: 'SaddleBrown', hex: '#8B4513' },
  { name: 'Chocolate', hex: '#D2691E' },
  { name: 'SandyBrown', hex: '#F4A460' },
  { name: 'PeachPuff', hex: '#FFDAB9' },
  { name: 'Peru', hex: '#CD853F' },
  { name: 'Linen', hex: '#FAF0E6' },
  { name: 'Bisque', hex: '#FFE4C4' },
  { name: 'DarkOrange', hex: '#FF8C00' },
  { name: 'BurlyWood', hex: '#DEB887' },
  { name: 'AntiqueWhite', hex: '#FAEBD7' },
  { name: 'Tan', hex: '#D2B48C' },
  { name: 'NavajoWhite', hex: '#FFDEAD' },
  { name: 'BlanchedAlmond', hex: '#FFEBCD' },
  { name: 'PapayaWhip', hex: '#FFEFD5' },
  { name: 'Moccasin', hex: '#FFE4B5' },
  { name: 'Orange', hex: '#FFA500' },
  { name: 'Wheat', hex: '#F5DEB3' },
  { name: 'OldLace', hex: '#FDF5E6' },
  { name: 'FloralWhite', hex: '#FFFAF0' },
  { name: 'DarkGoldenRod', hex: '#B8860B' },
  { name: 'GoldenRod', hex: '#DAA520' },
  { name: 'Cornsilk', hex: '#FFF8DC' },
  { name: 'Gold', hex: '#FFD700' },
  { name: 'LemonChiffon', hex: '#FFFACD' },
  { name: 'Khaki', hex: '#F0E68C' },
  { name: 'PaleGoldenRod', hex: '#EEE8AA' },
  { name: 'DarkKhaki', hex: '#BDB76B' },
  { name: 'Olive', hex: '#808000' },
  { name: 'Yellow', hex: '#FFFF00' },
  { name: 'LightGoldenRodYellow', hex: '#FAFAD2' },
  { name: 'Beige', hex: '#F5F5DC' },
  { name: 'LightYellow', hex: '#FFFFE0' },
  { name: 'Ivory', hex: '#FFFFF0' },
  { name: 'OliveDrab', hex: '#6B8E23' },
  { name: 'YellowGreen', hex: '#9ACD32' },
  { name: 'DarkOliveGreen', hex: '#556B2F' },
  { name: 'GreenYellow', hex: '#ADFF2F' },
  { name: 'Chartreuse', hex: '#7FFF00' },
  { name: 'LawnGreen', hex: '#7CFC00' },
  { name: 'DarkGreen', hex: '#006400' },
  { name: 'Green', hex: '#008000' },
  { name: 'ForestGreen', hex: '#228B22' },
  { name: 'Lime', hex: '#00FF00' },
  { name: 'LimeGreen', hex: '#32CD32' },
  { name: 'DarkSeaGreen', hex: '#8FBC8F' },
  { name: 'LightGreen', hex: '#90EE90' },
  { name: 'PaleGreen', hex: '#98FB98' },
  { name: 'HoneyDew', hex: '#F0FFF0' },
  { name: 'SeaGreen', hex: '#2E8B57' },
  { name: 'MediumSeaGreen', hex: '#3CB371' },
  { name: 'SpringGreen', hex: '#00FF7F' },
  { name: 'MintCream', hex: '#F5FFFA' },
  { name: 'MediumSpringGreen', hex: '#00FA9A' },
  { name: 'MediumAquaMarine', hex: '#66CDAA' },
  { name: 'Aquamarine', hex: '#7FFFD4' },
  { name: 'Turquoise', hex: '#40E0D0' },
  { name: 'LightSeaGreen', hex: '#20B2AA' },
  { name: 'MediumTurquoise', hex: '#48D1CC' },
  { name: 'DarkSlateGray', hex: '#2F4F4F' },
  // { name: 'DarkSlateGrey', hex: '#2F4F4F' },
  { name: 'Teal', hex: '#008080' },
  { name: 'DarkCyan', hex: '#008B8B' },
  { name: 'Aqua', hex: '#00FFFF' },
  { name: 'Cyan', hex: '#00FFFF' },
  { name: 'PaleTurquoise', hex: '#AFEEEE' },
  { name: 'LightCyan', hex: '#E0FFFF' },
  { name: 'Azure', hex: '#F0FFFF' },
  { name: 'DarkTurquoise', hex: '#00CED1' },
  { name: 'CadetBlue', hex: '#5F9EA0' },
  { name: 'PowderBlue', hex: '#B0E0E6' },
  { name: 'LightBlue', hex: '#ADD8E6' },
  { name: 'DeepSkyBlue', hex: '#00BFFF' },
  { name: 'SkyBlue', hex: '#87CEEB' },
  { name: 'LightSkyBlue', hex: '#87CEFA' },
  { name: 'SteelBlue', hex: '#4682B4' },
  { name: 'AliceBlue', hex: '#F0F8FF' },
  { name: 'DodgerBlue', hex: '#1E90FF' },
  { name: 'SlateGray', hex: '#708090' },
  // { name: 'SlateGrey', hex: '#708090' },
  { name: 'LightSlateGray', hex: '#778899' },
  // { name: 'LightSlateGrey', hex: '#778899' },
  { name: 'LightSteelBlue', hex: '#B0C4DE' },
  { name: 'CornflowerBlue', hex: '#6495ED' },
  { name: 'RoyalBlue', hex: '#4169E1' },
  { name: 'Navy', hex: '#000080' },
  { name: 'MidnightBlue', hex: '#191970' },
  { name: 'DarkBlue', hex: '#00008B' },
  { name: 'MediumBlue', hex: '#0000CD' },
  { name: 'Blue', hex: '#0000FF' },
  { name: 'Lavender', hex: '#E6E6FA' },
  { name: 'GhostWhite', hex: '#F8F8FF' },
  { name: 'SlateBlue', hex: '#6A5ACD' },
  { name: 'DarkSlateBlue', hex: '#483D8B' },
  { name: 'MediumSlateBlue', hex: '#7B68EE' },
  { name: 'MediumPurple', hex: '#9370DB' },
  { name: 'RebeccaPurple', hex: '#663399' },
  { name: 'BlueViolet', hex: '#8A2BE2' },
  { name: 'Indigo', hex: '#4B0082' },
  { name: 'DarkOrchid', hex: '#9932CC' },
  { name: 'DarkViolet', hex: '#9400D3' },
  { name: 'MediumOrchid', hex: '#BA55D3' },
  { name: 'Purple', hex: '#800080' },
  { name: 'DarkMagenta', hex: '#8B008B' },
  { name: 'Fuchsia', hex: '#FF00FF' },
  { name: 'Magenta', hex: '#FF00FF' },
  { name: 'Violet', hex: '#EE82EE' },
  { name: 'Plum', hex: '#DDA0DD' },
  { name: 'Thistle', hex: '#D8BFD8' },
  { name: 'Orchid', hex: '#DA70D6' },
  { name: 'MediumVioletRed', hex: '#C71585' },
  { name: 'HotPink', hex: '#FF69B4' },
  { name: 'LavenderBlush', hex: '#FFF0F5' },
  { name: 'PaleVioletRed', hex: '#DB7093' },
  { name: 'Crimson', hex: '#DC143C' },
  { name: 'Pink', hex: '#FFC0CB' },
  { name: 'LightPink', hex: '#FFB6C1' },
  // { name: 'Black', hex: '#000000' },
  { name: 'DimGray', hex: '#696969' },
  // { name: 'DimGrey', hex: '#696969' },
  { name: 'Gray', hex: '#808080' },
  { name: 'Grey', hex: '#808080' },
  { name: 'DarkGray', hex: '#A9A9A9' },
  // { name: 'DarkGrey', hex: '#A9A9A9' },
  { name: 'Silver', hex: '#C0C0C0' },
  { name: 'LightGray', hex: '#D3D3D3' },
  // { name: 'LightGrey', hex: '#D3D3D3' },
  { name: 'Gainsboro', hex: '#DCDCDC' },
  // { name: 'WhiteSmoke', hex: '#F5F5F5' },
]
